import { LoadingContext } from "context/LoadingContext";
import React, { ReactNode, useContext, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { BsFileEarmark } from "react-icons/bs";
import { FaCloudUploadAlt, FaTimes } from "react-icons/fa";
import showAlert from "shared/util/functions/alert/showAlert";
import { AnexoType } from "types/anexo/AnexoType";

interface CloudAnexoProps {
  onAddAnexo: (file: File) => Promise<any>;
  onRemoveAnexo: (anexo: AnexoType) => Promise<any>;
  onDownloadAnexo?: (anexo: AnexoType) => void;
  limiteDeAnexos?: number;
  tamanhoMaximoEmMegaByteDoAnexo?: number;
  isVisualizacao?: boolean;
  anexos: AnexoType[];
  /**As extensões passadas aqui irão ser previamente filtradas na maquina do usuário*/
  accept?: string;
  extensoesAceitas?: string[];
}

const AsyncCloudAnexo: React.FC<CloudAnexoProps> = (props) => {
  const [anexosSelecionados, setAnexosSelecionados] = useState<AnexoType[]>([]);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [verMaisAnexos, setVerMaisAnexos] = useState<boolean>(false);
  const { setLoading } = useContext(LoadingContext);

  useEffect(() => {
    if (props.anexos) {
      setAnexosSelecionados(props.anexos);
    }
  }, [props.anexos]);

  const openFileInput = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (!props.isVisualizacao) {
      e.stopPropagation();
      if (fileInputRef.current) {
        fileInputRef.current.click();
      }
    }
  };

  const renderUploadFile = () => {
    let className = "cloud-upload-input mb-1 ";
    if (!props.isVisualizacao) {
      className += "cursor-pointer";
    } else {
      className += "disabled";
    }
    return (
      <div className={className} onClick={openFileInput}>
        <FaCloudUploadAlt color="#b2b4b5" size={"2rem"} />
        <input
          ref={fileInputRef}
          type="file"
          id="cloud-upload-input"
          accept={props.accept || ""}
          onChange={adicionarAnexo}
          style={{ display: "none" }}
          value=""
        />
      </div>
    );
  };

  const adicionarAnexo = async (e: React.ChangeEvent<HTMLInputElement>) => {
    let msgErro = `É permitido anexar apenas ${props.limiteDeAnexos} 
      arquivos de ${props.tamanhoMaximoEmMegaByteDoAnexo}MB cada.`;

    if (props.limiteDeAnexos && anexosSelecionados.length >= props.limiteDeAnexos) {
      toast.error(msgErro);
      return;
    }
    const anexoSelecionado = e.target.files && e.target.files[0];
    if (anexoSelecionado) {
      if (props.tamanhoMaximoEmMegaByteDoAnexo && tamanhoMaximoDoAnexoExcedido(anexoSelecionado)) {
        toast.error(msgErro);
        return;
      }
      if (props.extensoesAceitas && extensaoDoArquivoNaoPermitida(anexoSelecionado)) {
        msgErro = `É permitido anexar somente arquivos nos formatos: ${props.extensoesAceitas.join(", ")}`;
        toast.error(msgErro);
        return;
      }
      try {
        const anexosAtualizados = [...anexosSelecionados];
        setLoading(true);
        await props.onAddAnexo(anexoSelecionado).then((resp) => {
          let anexo = resp.data as AnexoType;
          toast.success("Anexo adicionado com sucesso");
          anexosAtualizados.push(anexo);
          setAnexosSelecionados(anexosAtualizados);
        });
      } catch (error) {
        toast.error("Erro ao adicionar anexo");
      } finally {
        setLoading(false);
      }
    }
  };

  const extensaoDoArquivoNaoPermitida = (anexo: File): any => {
    if (props.extensoesAceitas) {
      let extensao = anexo.name.split(".").pop();
      if (extensao) {
        return !props.extensoesAceitas.includes(extensao);
      }
    }
    return false;
  };

  const tamanhoMaximoDoAnexoExcedido = (anexo: File) => {
    const tamanhoDoAnexoMb: number = transformarBytesEmMegaBytes(anexo.size);
    if (props.tamanhoMaximoEmMegaByteDoAnexo && props.tamanhoMaximoEmMegaByteDoAnexo < tamanhoDoAnexoMb) {
      return true;
    }
  };

  const transformarBytesEmMegaBytes = (tamanhoDoAnexoEmBytes: number): number => {
    const megabytes = tamanhoDoAnexoEmBytes / (1024 * 1024);
    return parseFloat(megabytes.toFixed(2));
  };

  const removerAnexo = (anexo: AnexoType, indexAnexo: number) => {
    const excluirAnexo = async () => {
      try {
        setLoading(true);
        await props
          .onRemoveAnexo(anexosSelecionados[indexAnexo])
          .then((resp) => toast.success("Anexo excluído com sucesso"));

        const anexosAtualizados = [...anexosSelecionados];
        anexosAtualizados.splice(indexAnexo, 1);
        if (anexosAtualizados.length === 1) {
          setVerMaisAnexos(false);
        }
        setAnexosSelecionados(anexosAtualizados);
      } catch (error) {
        toast.error("Erro ao excluir anexo");
      } finally {
        setLoading(false);
      }
    };

    showAlert({
      texto: "Você realmente deseja excluir?",
      tipoAlerta: "CONFIRMAR_CANCELAR_ALERT",
      onConfirm: () => excluirAnexo(),
    });
  };

  const renderArquivosSelecionados = () => {
    if (anexosSelecionados.length === 1) {
      return (
        <div>
          <div className="sub-titulo">Anexos</div>
          {renderAnexo(anexosSelecionados[0], 0)}
        </div>
      );
    } else {
      return (
        <div>
          {anexosSelecionados.length > 0 && <div className="sub-titulo">Anexos</div>}
          {!verMaisAnexos && anexosSelecionados.length > 1 && renderVerMaisAnexos()}
          {verMaisAnexos && renderListaDeAnexosSelecionados()}
          {anexosSelecionados.length > 1 && verMaisAnexos && renderVerMenosAnexos()}
        </div>
      );
    }
  };

  const renderVerMaisAnexos = (): ReactNode => {
    return (
      <div>
        {renderAnexo(anexosSelecionados[0], 0)}
        <div className="mt-2 cursor-pointer fit-content" onClick={() => setVerMaisAnexos(true)}>
          <span className="sub-titulo"> + Ver mais anexos</span>
        </div>
      </div>
    );
  };

  const renderVerMenosAnexos = () => {
    return (
      <div>
        <div className="mt-2 cursor-pointer fit-content" onClick={() => setVerMaisAnexos(false)}>
          <span className="sub-titulo"> - Ver menos anexos</span>
        </div>
      </div>
    );
  };

  const renderListaDeAnexosSelecionados = (): ReactNode => {
    return (
      <div>
        {anexosSelecionados.map((arquivo: AnexoType, index: number) => {
          return renderAnexo(arquivo, index);
        })}
      </div>
    );
  };

  const renderAnexo = (anexo: AnexoType, index: number): ReactNode => {
    return (
      <div key={`anexo-selecionado-${index}`}>
        <BsFileEarmark
          onClick={() => {
            props.onDownloadAnexo && props.onDownloadAnexo(anexo);
          }}
          className="cursor-pointer me-2"
          size="0.9rem"
        />
        <span>{anexo.nome}</span>
        {!props.isVisualizacao && (
          <FaTimes
            className="cursor-pointer ms-2"
            color="#e75458"
            size="0.9rem"
            onClick={(e) => removerAnexo(anexo, index)}
          />
        )}
      </div>
    );
  };

  return (
    <>
      <div>
        {renderUploadFile()}
        {renderArquivosSelecionados()}
      </div>
    </>
  );
};

export default AsyncCloudAnexo;
